import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { key: 1 }
const _hoisted_7 = { key: 2 }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { key: 1 }

import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { SampleCreateDocument, SampleUpdateDocument } from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import CodeValidation from '@/components/CodeValidation.vue';
import { DefaultCode } from '@/components/CodeValidation.vue';

// --- Code Validation component ref ---

export default /*@__PURE__*/_defineComponent({
  __name: 'SampleForm',
  props: {
    show: { type: Boolean },
    bucketId: {},
    sampleId: {},
    sampleName: {},
    sampleDescription: {},
    sampleFileR1: {},
    sampleFileR2: {},
    kitCode: {},
    warning: {},
    error: {}
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const codeValidation = ref<InstanceType<typeof CodeValidation>>();

// --- Component properties ---
const props = __props;

const sampleKitCode = computed<DefaultCode[]>(() => {
  return [
    {
      codeString: props.kitCode,
      unitsRequired: 1,
    },
  ];
});

// --- Component emits ---
const emits = __emit;

// --- Component state ---
const sampleName = ref<string>(props.sampleName);
const sampleDescription = ref<string>(props.sampleDescription);
const sampleFileR1 = ref<string>(props.sampleFileR1);
const sampleFileR2 = ref<string>(props.sampleFileR2);

const show = computed<boolean>(() => {
  return props.show;
});

const showWarning = computed<boolean>(() => {
  return props.warning !== null;
});

// --- Mutations State ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

// --- Mutation: Create Sample ---
const {
  mutate: executeSampleCreate,
  onDone: onSampleCreateDone,
  onError: onSampleCreateError,
} = useMutation(SampleCreateDocument);

onSampleCreateDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.sampleCreate) {
    console.debug('Sample created:', res.data.sampleCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Sample creation failed: ' + res.data;
    console.error('Sample creation failed:', res.data);
  }
});

onSampleCreateError((err) => {
  console.error('Error renaming data object:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Mutation: Update Sample ---
const {
  mutate: executeSampleUpdate,
  onDone: onSampleUpdateDone,
  onError: onSampleUpdateError,
} = useMutation(SampleUpdateDocument);

onSampleUpdateDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.sampleUpdate) {
    console.debug('Sample updated:', res.data.sampleUpdate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Sample update failed: ' + res.data;
    console.error('Sample update failed:', res.data);
  }
});

onSampleUpdateError((err) => {
  console.error('Error updating sample:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function mutationRequested() {
  if (props.error) return;
  if (!sampleName.value) {
    mutationError.value = 'You must provide sample name';
    console.error('Sample name not provided');
    return;
  }
  if (
    codeValidation.value?.codeValidationItems.length === 0 ||
    !codeValidation.value?.codeValidationItems[0].code?.kit?.id
  ) {
    mutationError.value = 'You must provide a valid kit code';
    console.error('Kit code/id not provided');
    return;
  }

  // set progress state
  mutationInProgress.value = true;
  mutationError.value = null;

  // create vs. udpate
  if (props.sampleId) {
    console.debug('Updating sample:', props.sampleId);
    executeSampleUpdate({
      id: props.sampleId,
      kitId: codeValidation.value.codeValidationItems[0].code.kit.id,
      name: sampleName.value,
      description: sampleDescription.value,
      fileR1: sampleFileR1.value,
      fileR2: sampleFileR2.value,
    });
  } else {
    console.debug('Creating new sample:', sampleName.value);
    executeSampleCreate({
      bucketId: props.bucketId,
      kitId: codeValidation.value.codeValidationItems[0].code.kit.id,
      name: sampleName.value,
      description: sampleDescription.value,
      fileR1: sampleFileR1.value,
      fileR2: sampleFileR2.value,
    });
  }
}

function handleCloseModal() {
  emits('close');
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((show).value = $event)),
      onHide: handleCloseModal
    }, {
      title: _withCtx(() => [
        (props.sampleId)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createVNode(_component_font_awesome_icon, {
                icon: "pencil",
                class: "me-2"
              }),
              _cache[9] || (_cache[9] = _createTextVNode(" Edit sample ", -1))
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(_component_font_awesome_icon, {
                icon: "plus",
                class: "me-2"
              }),
              _cache[10] || (_cache[10] = _createTextVNode(" New sample ", -1))
            ], 64))
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: handleCloseModal
        }, {
          default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(BButton), {
          variant: "primary",
          onClick: mutationRequested,
          disabled: 
            !!props.error ||
            !sampleName.value ||
            codeValidation.value?.failure ||
            mutationInProgress.value ||
            mutationShowSuccess.value
          
        }, {
          default: _withCtx(() => [
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                  (props.sampleId)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Updating Sample"))
                    : (_openBlock(), _createElementBlock("span", _hoisted_3, "Creating Sample")),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                    (props.sampleId)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_5, "Sample Updated "))
                      : (_openBlock(), _createElementBlock("span", _hoisted_6, "Sample Created ")),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_7, [
                    (props.sampleId)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Update Sample"))
                      : (_openBlock(), _createElementBlock("span", _hoisted_9, "Create Sample"))
                  ]))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _createVNode(_unref(BFormGroup), {
            label: "Sample name",
            "label-for": "sample-name",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: sampleName.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((sampleName).value = $event)),
                id: "sample-name",
                name: "sample-name",
                placeholder: "Enter a name for the sample",
                required: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "Sample description",
            "label-for": "sample-description",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormTextarea), {
                modelValue: sampleDescription.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sampleDescription).value = $event)),
                id: "sample-description",
                name: "sample-description",
                placeholder: "A brief description of the sample."
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "R1 File",
            "label-for": "sample-file-R1",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: sampleFileR1.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((sampleFileR1).value = $event)),
                label: "File R1",
                placeholder: "Enter the R1 file name",
                required: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), {
            label: "R2 File",
            "label-for": "sample-file-R2",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                modelValue: sampleFileR2.value,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((sampleFileR2).value = $event)),
                label: "R2 File",
                placeholder: "Enter the R2 file name",
                required: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(CodeValidation, {
            class: "mt-3",
            ref_key: "codeValidation",
            ref: codeValidation,
            "label-text": "Kit code",
            "button-text": "Apply",
            "default-codes": sampleKitCode.value,
            "require-kit": true,
            "require-explicit-counts": false,
            "required-coupon-units-count": Number(0),
            "required-coupon-type-name": null,
            "requried-coupon-units-name": null,
            "required-license-type-name": null,
            "immediate-validation": true,
            "hide-component-message": false
          }, null, 8, ["default-codes", "required-coupon-units-count"]),
          (codeValidation.value)
            ? (_openBlock(), _createBlock(_unref(BAlert), {
                key: 0,
                modelValue: codeValidation.value.success,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((codeValidation.value.success) = $event)),
                variant: "success",
                class: "mt-3"
              }, {
                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                  _createTextVNode(" Please double-check the kit code before proceeding. Incorrect kit association may lead to down-stream analysis errors. Correct kit selection provides barcode specification and other important information which is critical for achieving accurate results. ", -1)
                ]))]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(BAlert), {
            "model-value": !!props.error,
            variant: "danger",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(props.error), 1)
            ]),
            _: 1
          }, 8, ["model-value"]),
          _createVNode(_unref(BAlert), {
            modelValue: showWarning.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showWarning).value = $event)),
            variant: "warning",
            class: "mt-3"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(props.warning), 1)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_unref(BAlert), {
            modelValue: mutationError.value,
            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((mutationError).value = $event)),
            variant: "danger",
            dismissible: "",
            class: "mt-3",
            onDismissed: _cache[7] || (_cache[7] = ($event: any) => (mutationError.value = null))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(mutationError.value), 1)
            ]),
            _: 1
          }, 8, ["modelValue"])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})